import type { FormValues } from '@/lib/types';
import type { TestData } from '@/components/sop-intake/types';
import type { SopAnalysisResult, TestLateralization } from '@/components/sop-intake/sop-analyzer/types';

const isBlankValue = (value: unknown): boolean =>
  value === null || value === undefined || value === '' || value === 'Select...';

export function toVisibleTestKeySet(keys: string[]): ReadonlySet<string> {
  return new Set(keys);
}

export function filterLateralizationTests(
  tests: TestLateralization[],
  visibleTestKeys: ReadonlySet<string>
): TestLateralization[] {
  return tests.filter((test) => visibleTestKeys.has(test.testKey));
}

export function filterSpectrumDefinitions(
  analysis: SopAnalysisResult,
  visibleTestKeys: ReadonlySet<string>
) {
  return analysis.spectrum.definitions.filter((definition) => visibleTestKeys.has(definition.key));
}

export function isCervicalBatteryVisible(visibleTestKeys: ReadonlySet<string>): boolean {
  return (
    visibleTestKeys.has('CervicalAxialRotation') || visibleTestKeys.has('CervicalLateralFlexion')
  );
}

export function isGroundingBatteryVisible(visibleTestKeys: ReadonlySet<string>): boolean {
  return visibleTestKeys.has('GroundingPerformance');
}

/** Bilateral sides completed within the current visible battery. */
export function countVisibleCompletedSides(
  formValues: FormValues,
  visibleTestKeys: ReadonlySet<string>,
  testDataMap: TestData
): number {
  let count = 0;

  for (const key of visibleTestKeys) {
    const item = testDataMap[key];
    if (!item) continue;

    if (item.type === 'grounding') {
      const mode =
        String(formValues.GroundingMode || '').toLowerCase() === 'perceived'
          ? 'perceived'
          : 'dynamometer';
      for (const side of ['L', 'R'] as const) {
        const preferredKey =
          mode === 'perceived'
            ? `${side}_GroundingPerformance_perceived`
            : `${side}_GroundingPerformance_actual`;
        const candidates = [
          formValues[preferredKey],
          formValues[`${side}_GroundingPerformance`],
        ];
        if (candidates.some((value) => !isBlankValue(value))) {
          count += 1;
        }
      }
      continue;
    }

    if (item.type === 'select' || item.type === 'text') continue;

    for (const side of ['L', 'R'] as const) {
      if (!isBlankValue(formValues[`${side}_${key}`])) {
        count += 1;
      }
    }
  }

  return count;
}

/** Total measurable sides in the visible battery (for progress denominator). */
export function countVisibleTotalSides(
  visibleTestKeys: ReadonlySet<string>,
  testDataMap: TestData
): number {
  let count = 0;

  for (const key of visibleTestKeys) {
    const item = testDataMap[key];
    if (!item) continue;

    if (item.type === 'grounding') {
      count += 2;
      continue;
    }

    if (item.type === 'select' || item.type === 'text') continue;

    count += 2;
  }

  return count;
}
